import { useEffect, useState } from 'react';
import axios from 'axios';

import SignInPage from '../modules/auth/pages/SignInPage';
import ForbiddenPage from '../general-pages/ForbiddenPage';
import Page404 from '../general-pages/Page404';

const currentRequests: Record<string, Promise<any>> = {};

export const getData = <T = any>(
  endPoint: string,
  endPointData: any = null,
  setEndPointData: (arg: T | null) => void,
) => {
  if (!endPoint) return endPointData;

  const preload = (window as any).preloadedApi;
  if (preload[endPoint]) {
    setEndPointData(preload[endPoint].body);
    console.log('get from preload', endPoint, preload[endPoint]);
    if (preload[endPoint]?.status === 404) {
      window.location.replace(Page404.getRoute());
    }

    // @ts-expect-error
    delete window['preloadedApi'][endPoint];
  } else {
    axios
      .get(endPoint)
      .then((response) => {
        setEndPointData(response.data);
      })
      .catch((error) => {
        const { response } = error;
        if (response?.status === 301) {
          window.location.replace(SignInPage.getRoute());
        } else if (response?.status === 403) {
          window.location.replace(ForbiddenPage.getRoute());
        } else {
          setEndPointData(null);
        }
      })
      .finally(() => {
        // Workaround for caching same requests that are close
      });
  }
};

const useFetchEndPoint = <T extends any>(
  endPoint: string,
  defaultValue: T | null = null,
  update: any[] = [],
) => {
  const [endPointData, setEndPointData] = useState<T | null>(defaultValue);
  useEffect(() => {
    getData(endPoint, endPointData, setEndPointData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, update);

  return endPointData;
};

export default useFetchEndPoint;

export interface UseGetWithRefreshOptions {
  defaultValue?: any;
  running?: boolean;
}

export const useGetWithRefetch = <T extends any>(
  endPoint: string | null | undefined,
  options: UseGetWithRefreshOptions = {},
): [T | null, { isLoading: boolean; refetch: () => Promise<unknown> }] => {
  const { running = true, defaultValue = null } = options;
  const [endPointData, setEndPointData] = useState(defaultValue);
  const [isLoading, setIsLoading] = useState(false);

  const refetch = () => {
    return new Promise((res) => {
      const out = getData(endPoint || '', endPointData, (d) => {
        setEndPointData(d);
        res(d);
      });

      if (out) {
        res(out);
      }
    });
  };

  useEffect(() => {
    if (!isLoading && running) {
      setIsLoading(true);
      refetch().finally(() => {
        setIsLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endPoint, running]);

  if (!running || !endPoint) {
    return [
      defaultValue,
      { refetch: () => Promise.resolve(), isLoading: false },
    ];
  }

  return [endPointData, { refetch, isLoading }];
};
